// components/BatchProcessing/BatchResults.jsx
import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert
} from '@mui/material';
import {
  Download as DownloadIcon,
  RestartAlt as RestartIcon,
  ArrowForward as ArrowForwardIcon ,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const BatchResults = ({ batchStatus, fatalError, onDownload, onReset, onBack }) => {

  const sortedTasks = React.useMemo(() => {
    if (!batchStatus?.tasks_status || !Array.isArray(batchStatus.tasks_status)) return [];
    
    return [...batchStatus.tasks_status].sort((a, b) => {
      const idA = parseInt(a.task_id) || 0;
      const idB = parseInt(b.task_id) || 0;
      
      if (idA !== 0 && idB !== 0) {
        return idA - idB;
      }
      
      const strA = a.task_id || '';
      const strB = b.task_id || '';
      return strA.localeCompare(strB);
    });
  }, [batchStatus?.tasks_status]);
  const { 
    status, 
    tasks_status = [], 
    completed_tasks, 
    failed_tasks, 
    total_tasks 
  } = batchStatus;

  const successfulTasks = tasks_status.filter(task => task.status === 'COMPLETED').length;
  const navigate = useNavigate();
  const hasSuccessfulTasks = successfulTasks > 0;
    if (fatalError) {
      return (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h6" color="error" gutterBottom>
            Erro no Processamento
          </Typography>

          <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
            {fatalError}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 300, margin: '0 auto' }}>
            <Button variant="contained" color="primary" onClick={onReset}>
              Novo Processamento
            </Button>
          </Box>
        </Box>
      );
    }
    if (!batchStatus) return null;
  const getTaskStatusIcon = (taskStatus) => {
    return taskStatus === 'COMPLETED' ? 
      <SuccessIcon color="success" fontSize="small" /> : 
      <ErrorIcon color="error" fontSize="small" />;
  };
  const getTaskDisplayName = (task, index) => {
    // if (task.task_name && task.task_name.trim() !== '') {
    //   return task.task_name;
    // }
    
    return `Tarefa ${index + 1}`;
  };
  const getFileName = (path) => {
    if (!path) return '—';
    return path.split('/').pop() || path.split('\\').pop();
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        Resultados do Processamento em Lote
      </Typography>

      {/* Resumo do Lote */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Resumo do Lote
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Status Geral:</Typography>
                  <Chip 
                    label={status} 
                    color={
                      status === 'COMPLETED' ? 'success' : 
                      status === 'PARTIALLY_COMPLETED' ? 'warning' : 'error'
                    }
                    size="small"
                  />
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Tarefas Concluídas:</Typography>
                  <Typography variant="body2" fontWeight="bold" color="success.main">
                    {successfulTasks}/{total_tasks}
                  </Typography>
                </Box>
                
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2">Tarefas com Falha:</Typography>
                  <Typography variant="body2" fontWeight="bold" color="error.main">
                    {failed_tasks || 0}/{total_tasks}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Ações
              </Typography>
              
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<DownloadIcon />}
                  onClick={onDownload}
                  disabled={!hasSuccessfulTasks}
                  fullWidth
                >
                  Baixar Todos os Resultados
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<RestartIcon />}
                  onClick={onReset}
                  fullWidth
                >
                  Novo Processamento
                </Button>
                
                <Button
                  variant="contained"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => {
                    const firstCompletedTask = tasks_status.find(task => task.status === 'COMPLETED');
                    if (firstCompletedTask) {
                      navigate('/Forms', {
                        state: {
                          taskId: firstCompletedTask.task_id,
                          roadName: firstCompletedTask.task_name,
                          snippet: '',
                        }
                      });
                    }
                  }}
                  disabled={!hasSuccessfulTasks}
                  fullWidth
                >
                  IR PARA CODIFICAÇÃO
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Alertas */}
      {status === 'COMPLETED' && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Todas as tarefas foram processadas com sucesso! Você pode baixar os resultados.
        </Alert>
      )}
      
      {status === 'PARTIALLY_COMPLETED' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {successfulTasks} de {total_tasks} tarefas foram concluídas com sucesso. 
          Apenas os resultados das tarefas bem-sucedidas estarão disponíveis para download.
        </Alert>
      )}
      
      {status === 'FAILED' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Todas as tarefas falharam. Verifique os logs abaixo e tente novamente.
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Detalhes das Tarefas
          </Typography>
          
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tarefa</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Nome da Via</TableCell>
                  <TableCell>ID</TableCell>
                  <TableCell>Arquivo do Eixo</TableCell>
                  <TableCell>Direção</TableCell>
                  <TableCell>Arquivos Gerados</TableCell>
                  <TableCell>Mensagem</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedTasks.map((task, index) => (
                  <TableRow key={task.task_id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getTaskStatusIcon(task.status)}
                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                          {getTaskDisplayName(task, index)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={task.status}
                        color={task.status === 'COMPLETED' ? 'success' : 'error'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {task.task_name || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {task.task_id || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {task.axis_file_name.split('_').slice(0, -1).join('_') + ".gpkg" || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {task.eixo_direction || 'Automático'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {task.status === 'COMPLETED' ? 
                          'Estaqueamento 1km, 20m e Planilha' : 
                          'Nenhum arquivo gerado'
                        }
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="caption" 
                        color={task.error_message ? "error" : "textSecondary"}
                        sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}
                      >
                        {task.error_message || 'Processamento concluído com sucesso'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default BatchResults;